import React from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CountUp from "react-countup";
import {
    ResultsPageContainer,
    ResultsPageHeader,
    ResponsiveTableWrap,
    ResultsSectionPaper,
} from "../admin/examRelated/resultsUi";

export { ResultsPageContainer as StudentPageContainer };
export { ResultsPageHeader as StudentPageHeader };
export { ResponsiveTableWrap, ResultsSectionPaper };

export function StudentStatCard({ title, value, hint, icon: Icon, color = "primary", loading }) {
    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                bgcolor: "background.paper",
            }}
        >
            <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {title}
                            </Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1 }}>
                                <CountUp end={Number(value) || 0} duration={1.6} preserveValue />
                            </Typography>
                            {hint ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                                    {hint}
                                </Typography>
                            ) : null}
                        </Box>
                        {Icon ? (
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 44,
                                    height: 44,
                                    bgcolor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.1),
                                    color: (t) => t.palette[color]?.main ?? t.palette.primary.main,
                                }}
                            >
                                <Icon fontSize="small" />
                            </Avatar>
                        ) : null}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

export function StudentViewToggle({ value, onChange, options }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 0.5,
                mb: 2,
                borderRadius: 2,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                display: "inline-flex",
            }}
        >
            <ToggleButtonGroup
                exclusive
                size="small"
                value={value}
                onChange={(_, v) => v && onChange(v)}
                sx={{ "& .MuiToggleButton-root": { px: 2, fontWeight: 600, textTransform: "none" } }}
            >
                {options.map(({ value: v, label, icon }) => (
                    <ToggleButton key={v} value={v}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            {icon}
                            <span>{label}</span>
                        </Stack>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Paper>
    );
}

export function StudentEmptyState({ title, description, icon: Icon }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
            }}
        >
            {Icon ? <Icon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} /> : null}
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
                {description}
            </Typography>
        </Paper>
    );
}

export function StudentPageLoading() {
    return (
        <ResultsPageContainer maxWidth="lg">
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        </ResultsPageContainer>
    );
}
